/**
 * Module: screens/creator/steps/CitiesStep
 * Role: Wizard Step 7/8 — audience cities (chips of 8 popular + custom city
 *       input) and the wizard's final submit (FTR-1131 round 3, design
 *       Eqq6AbBPpm1zPPyErlkgJQ chat16).
 *
 *       Round-3 fixes (founder report 2026-06-07):
 *       1. **Keyboard overlap** — «снизу влезает клавиатура и она все как бы
 *          двигает кнопку отправить вверх и загораживает к хуям поле ввода».
 *          We now hide WizardCtaBar while the on-screen keyboard is up
 *          (`useKeyboardHeight() > 0`) and scroll the focused input into view
 *          so the user can actually see what they're typing. CTA reappears
 *          after blur.
 *       2. **Caret invisible** — «курсор как будто бы белый, надпись „Введите
 *          город" не исчезает, курсор слева в начале строки вообще не видно».
 *          The default `caret-color` follows the OS theme, which renders white
 *          inside the TG WebView dark-themed surface; force ink (#2D2518)
 *          across all inputs on this screen.
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - screens/creator/useCreatorOptions:useCreatorOptions (BUG-1144 auth-gated fetch)
 *   - api/creator:submitVerifyApply, CreatorVerifyRateLimitError
 *   - hooks/useKeyboardHeight:useKeyboardHeight
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/_chrome/WizardShell
 *   - components/atoms/PressBtn (compact «Добавить» — INK, not terracotta)
 *
 * Used by: CreatorOnboardingScreen.tsx
 * ADR-023: no window.Telegram / platform literals outside src/platform/
 * ADR-022 §7: no openExternal(BOT_LINK)
 */

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  submitVerifyApply,
  CreatorVerifyRateLimitError,
} from '../../../api/creator';
import { useCreatorOptions } from '../useCreatorOptions';
import { useWizardStore } from '../store';
import { useKeyboardHeight } from '../../../hooks/useKeyboardHeight';
import { PressBtn } from '../../../components/atoms/PressBtn';
import { P, T } from '../../../styles/tokens';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from '../_chrome/WizardShell';

// Popular-city quick-select (design L385 `POPULAR_CITIES`, 8 entries).
const POPULAR_CITIES = [
  'Москва',
  'Санкт-Петербург',
  'Новосибирск',
  'Екатеринбург',
  'Казань',
  'Нижний Новгород',
  'Краснодар',
  'Сочи',
];

export default function CitiesStep() {
  const navigate = useNavigate();
  const { draft, setProfile, setRateLimit } = useWizardStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const keyboardHeight = useKeyboardHeight();
  const keyboardOpen = keyboardHeight > 0;

  const [cities, setCities] = useState<string[]>(draft.cities);
  const [custom, setCustom] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { options, isLoading: optLoading } = useCreatorOptions();

  const limits = options?.limits ?? { max_cities: 3, city_max_chars: 50, max_formats: 5 };
  const atMax = cities.length >= limits.max_cities;

  const customCities = useMemo(
    () => cities.filter((c) => !POPULAR_CITIES.includes(c)),
    [cities],
  );

  const handleBack = () => navigate(-1);

  function toggle(c: string) {
    setCities((p) =>
      p.includes(c)
        ? p.filter((x) => x !== c)
        : p.length < limits.max_cities
          ? [...p, c]
          : p,
    );
  }

  function addCustom() {
    const v = custom.trim();
    if (!v) return;
    if (v.length > limits.city_max_chars) {
      setError(`Название города не более ${limits.city_max_chars} символов`);
      return;
    }
    if (atMax || cities.includes(v)) return;
    setError(null);
    setCities((p) => [...p, v]);
    setCustom('');
  }

  async function handleSubmit() {
    setError(null);
    setProfile({ cities });
    setSubmitting(true);
    try {
      await submitVerifyApply({
        selfie_base64: draft.selfie_dataUrl,
        vk_url: draft.vk_url || undefined,
        tiktok_url: draft.tiktok_url || undefined,
        profile_data: {
          categories: draft.categories,
          formats: draft.formats,
          gender: draft.gender || undefined,
          age_range: draft.age_range || undefined,
          cities,
        },
      });
      navigate('/creator-status', { replace: true });
    } catch (err) {
      if (err instanceof CreatorVerifyRateLimitError) {
        setRateLimit(err.retryAfter ? Date.now() + err.retryAfter * 1000 : null);
        navigate('/creator-status', { replace: true });
      } else {
        // BUG-1153: surface the backend's user-facing reason (set by apiFetch as
        // err.userMessage) — but ONLY if it is Cyrillic. The product is Russian; all
        // user-facing backend texts are Cyrillic. This one guard filters out English
        // technical strings ("vk_url host must be vk.com or vk.ru"), machine codes
        // (trust_too_low, VALIDATION_ERROR) and Pydantic lists. Otherwise → neutral generic.
        const e = err as Error & { userMessage?: string };
        const presentable =
          e.userMessage && /[а-яё]/i.test(e.userMessage) ? e.userMessage : null;
        setError(presentable ?? 'Не удалось отправить заявку. Попробуйте ещё раз.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Force ink caret on every input on this screen — default OS caret renders
  // white inside the TG WebView dark theme (founder report 2026-06-07).
  const inputStyle = {
    width: '100%',
    padding: '13px 14px',
    borderRadius: 12,
    border: `1.5px solid ${P.line}`,
    font: T.callout,
    color: P.ink,
    caretColor: P.ink,
    background: P.card,
    outline: 'none',
    boxSizing: 'border-box' as const,
    opacity: atMax ? 0.5 : 1,
  };

  return (
    <WizardFrame>
      <WizardHeader title="Верификация" onBack={handleBack} />
      <WizardStepStrip current={7} total={8} />
      <WizardBody
        style={{
          // Reserve space at the bottom equal to the on-screen keyboard so
          // the focused input scrolls fully into view above it.
          paddingBottom: keyboardOpen ? `${keyboardHeight + 24}px` : 8,
        }}
      >
        <div style={{ paddingTop: 10 }}>
          <div style={{ font: T.title2, color: P.ink, marginBottom: 4 }}>
            Города аудитории
          </div>
          <div style={{ font: T.footnote, color: P.inkSoft }}>
            Выберите до {limits.max_cities} городов — необязательно
          </div>
        </div>

        {optLoading && (
          <div style={{ color: P.inkSoft, font: T.subhead }}>Загрузка…</div>
        )}

        {!optLoading && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {POPULAR_CITIES.map((c) => {
              const active = cities.includes(c);
              const blocked = !active && atMax;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => !blocked && toggle(c)}
                  style={{
                    padding: '9px 15px',
                    borderRadius: 20,
                    border: `1.5px solid ${active ? P.terracotta : P.line}`,
                    background: active ? P.terraSoft : P.card,
                    color: blocked ? P.inkSoft : active ? P.terraDark : P.ink,
                    font: T.subhead,
                    fontWeight: active ? 600 : 400,
                    cursor: blocked ? 'default' : 'pointer',
                    opacity: blocked ? 0.45 : 1,
                    transition: 'background 0.15s, border-color 0.15s',
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>
        )}

        <div>
          <label
            style={{
              font: T.footnote,
              color: P.inkSoft,
              display: 'block',
              marginBottom: 6,
            }}
          >
            Другой город
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              ref={inputRef}
              type="text"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onFocus={() => {
                // Scroll the input above the keyboard once iOS finishes the
                // animation (300ms is enough on all current TG WebView builds).
                setTimeout(() => {
                  inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }, 300);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addCustom(); }
              }}
              placeholder="Введите город"
              disabled={atMax}
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            />
            <PressBtn
              onClick={addCustom}
              disabled={atMax || !custom.trim()}
              bg={P.ink}
              fg="#ffffff"
              full={false}
              size="md"
              style={{ flexShrink: 0, paddingLeft: 20, paddingRight: 20 }}
            >
              Добавить
            </PressBtn>
          </div>
        </div>

        {customCities.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {customCities.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => toggle(c)}
                style={{
                  padding: '9px 15px',
                  borderRadius: 20,
                  border: `1.5px solid ${P.terracotta}`,
                  background: P.terraSoft,
                  color: P.terraDark,
                  font: T.subhead,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {c} ✕
              </button>
            ))}
          </div>
        )}

        <div style={{ font: T.caption, color: P.inkSoft }}>
          Это последний шаг — после отправки заявка уйдёт на проверку.
        </div>

        {error && (
          <div
            style={{
              borderRadius: 12,
              background: 'rgba(209,67,67,0.08)',
              border: '1px solid rgba(209,67,67,0.25)',
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ font: T.footnote, color: P.error }}>{error}</div>
            <button
              type="button"
              onClick={() => navigate('/profile')}
              style={{
                alignSelf: 'flex-start',
                font: T.subhead,
                fontWeight: 600,
                color: P.terraDark,
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              Закрыть и вернуться в профиль
            </button>
          </div>
        )}
      </WizardBody>
      {/* Hide the sticky CTA while the keyboard is up — otherwise it climbs
          on top of the focused input and covers it. User can dismiss the
          keyboard (tap outside, hit return) and the CTA returns instantly. */}
      {!keyboardOpen && (
        <WizardCtaBar
          label={submitting ? 'Отправка…' : 'Отправить заявку'}
          onClick={handleSubmit}
          disabled={submitting}
        />
      )}
    </WizardFrame>
  );
}
